import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { fromRow } from '../lib/rowMappers';
import type { ThemeStore, StudioConfig } from './types';

// Helper to calculate contrast color (Black or White) based on hex background
export const getContrastColor = (hex: string): string => {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? '#000000' : '#ffffff';
};

export const useThemeStore = create<ThemeStore>()((set, get) => ({
  config: null,
  isThemeLoading: false,

  setTheme: (config: StudioConfig | null) => {
    set({ config });
    if (config) {
      const root = document.documentElement;
      
      // Inject Colors
      root.style.setProperty('--accent-primary', config.colors.primary);
      root.style.setProperty('--accent-secondary', config.colors.secondary);
      root.style.setProperty('--bg-primary', config.colors.background);
      root.style.setProperty('--bg-secondary', config.colors.surface);
      
      // Calculate Contrast for Buttons (assuming primary is used for buttons)
      root.style.setProperty('--bg-primary-contrast', getContrastColor(config.colors.primary));
      
      // Inject Typography
      root.style.setProperty('--font-family', config.typography.fontFamily);
      
      // Dynamic Favicon & Title
      document.title = config.brandName;
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = config.logoUrl;
    }
  },

  syncTheme: async (studioId: string) => {
    set({ isThemeLoading: true });
    try {
      // Supabase `studios` table (jsonb colors/typography/features) — Firebase is gone.
      const { data } = await supabase.from('studios').select('*').eq('id', studioId).maybeSingle();
      if (data) get().setTheme(fromRow<StudioConfig>(data));
    } catch (error) {
      console.error('Theme Sync Error:', error);
    } finally {
      set({ isThemeLoading: false });
    }
  }
}));
